import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminNavbar from '../../components/AdminNavbar';
import axios from '../../api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, X, Image as ImageIcon, Check, Loader2, Calendar, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardFooter } from '../../components/ui/card';

const AdminEvents = () => {
    const { admin } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image: '',
        date: '',
        is_active: true
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        if (!admin) {
            navigate('/admin/login');
            return;
        }
        fetchEvents();
    }, [admin, navigate]);

    const fetchEvents = async () => {
        try {
            const response = await axios.get('/events/all');
            setEvents(response.data);
            setLoading(false);
        } catch (error) {
            logger.error('Error fetching events:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({
            ...formData,
            [e.target.name]: value
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5000000) { // 5MB limit
                alert('File size too large. Please upload an image under 5MB.');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setImagePreview(base64String);
                setFormData(prev => ({ ...prev, image: base64String }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Removed uploadImage function - using Base64 directly

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingEvent) {
                await axios.put(`/events/${editingEvent.id}`, formData);
            } else {
                await axios.post('/events', formData);
            }

            fetchEvents();
            resetForm();
            setShowModal(false);
        } catch (error) {
            logger.error('Error saving event:', error);
            alert('Failed to save event. Ensure image is not too large.');
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            title: event.title,
            description: event.description,
            image: event.image || '',
            date: event.date ? event.date.split('T')[0] : '',
            is_active: event.is_active
        });
        setImageFile(null);
        setImagePreview(event.image || '');
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this event?')) {
            try {
                await axios.delete(`/events/${id}`);
                fetchEvents();
            } catch (error) {
                logger.error('Error deleting event:', error);
                alert('Failed to delete event');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            image: '',
            date: '',
            is_active: true
        });
        setEditingEvent(null);
        setImageFile(null);
        setImagePreview('');
    };

    if (!admin) return null;

    return (
        <div className="min-h-screen bg-background pb-20">
            <AdminNavbar />

            <div className="container mx-auto px-6 py-8">
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
                            Events Management
                        </h1>
                        <p className="text-gray-400 mt-2">Manage special events, stalls, and announcements</p>
                    </div>
                    <Button
                        onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}
                        className="bg-gold-500 text-black hover:bg-gold-400 font-bold px-6 h-12 rounded-full shadow-lg shadow-gold-500/20 transition-all hover:scale-105"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Add New Event
                    </Button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                        {events.length === 0 ? (
                            <div className="col-span-full text-center py-12 text-gray-500 border border-white/10 rounded-xl bg-black/20">
                                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No events found. Create one to get started!</p>
                            </div>
                        ) : (
                            events.map((event, index) => (
                                <motion.div
                                    key={event.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Card className="overflow-hidden bg-black/40 border-gold-500/20 backdrop-blur-sm hover:border-gold-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/10 group h-full flex flex-col">
                                        <div className="relative h-48 overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10 opacity-60" />
                                            {event.image ? (
                                                <img
                                                    src={event.image}
                                                    alt={event.title}
                                                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                                    <ImageIcon className="w-12 h-12 text-gray-600" />
                                                </div>
                                            )}
                                            <div className="absolute top-3 right-3 z-20">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1 ${event.is_active ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                                                    {event.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                                                    {event.is_active ? 'Active' : 'Hidden'}
                                                </span>
                                            </div>
                                            {event.date && (
                                                <div className="absolute bottom-3 left-3 z-20">
                                                    <span className="px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-white text-xs font-medium flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {new Date(event.date).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <CardContent className="p-5 flex-1">
                                            <h3 className="text-xl font-bold text-gold-200 line-clamp-1 mb-2">{event.title}</h3>
                                            <p className="text-gray-400 text-sm line-clamp-3 mb-4">{event.description}</p>
                                        </CardContent>

                                        <CardFooter className="p-4 pt-0 grid grid-cols-2 gap-3 mt-auto">
                                            <Button
                                                variant="outline"
                                                onClick={() => handleEdit(event)}
                                                className="w-full border-blue-500/30 text-blue-400 hover:text-white hover:bg-blue-600 hover:border-transparent transition-all"
                                            >
                                                <Edit className="w-4 h-4 mr-2" /> Edit
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={() => handleDelete(event.id)}
                                                className="w-full border-red-500/30 text-red-400 hover:text-white hover:bg-red-600 hover:border-transparent transition-all"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                                            </Button>
                                        </CardFooter>
                                    </Card>
                                </motion.div>
                            ))
                        )}
                    </motion.div>
                )}
            </div>

            <AnimatePresence>
                {showModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => { setShowModal(false); resetForm(); }}
                            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-gray-900 border border-gold-500/30 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
                        >
                            <div className="flex justify-between items-center p-6 border-b border-white/10 bg-black/40">
                                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
                                    {editingEvent ? 'Edit Event' : 'Create New Event'}
                                </h2>
                                <Button variant="ghost" size="icon" onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-white">
                                    <X className="w-6 h-6" />
                                </Button>
                            </div>

                            <div className="p-6 overflow-y-auto custom-scrollbar">
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gold-400">Event Title</label>
                                        <Input
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="e.g., Food Festival Stall"
                                            className="bg-black/20 border-white/10 focus:border-gold-500/50"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                                            <FileText className="w-4 h-4" /> Description
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            required
                                            rows="4"
                                            className="w-full rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                                            placeholder="Details about the event..."
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                                            <Calendar className="w-4 h-4" /> Event Date (Optional)
                                        </label>
                                        <Input
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleInputChange}
                                            className="bg-black/20 border-white/10 focus:border-gold-500/50 w-full"
                                        />
                                    </div>

                                    {/* Image Upload */}
                                    <div className="space-y-4 border border-white/10 rounded-lg p-4 bg-black/20">
                                        <label className="text-sm font-medium text-gold-400 flex items-center gap-2">
                                            <ImageIcon className="w-4 h-4" /> Cover Image
                                        </label>
                                        <div className="flex items-start gap-4">
                                            {imagePreview && (
                                                <img src={imagePreview} alt="Preview" className="w-24 h-24 object-cover rounded-lg border border-gold-500/30" />
                                            )}
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="bg-transparent border-white/10 cursor-pointer file:bg-gold-500/10 file:text-gold-500 file:border-0 file:rounded-md file:px-2 file:py-1 file:mr-4 file:font-semibold hover:file:bg-gold-500/20"
                                            />
                                        </div>
                                    </div>

                                    {/* Status Toggle */}
                                    <div className="flex items-center justify-between bg-black/20 p-4 rounded-lg border border-white/10">
                                        <span className="text-white font-medium">Visibility Status</span>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                                            <span className="ml-3 text-sm font-medium text-gray-300">
                                                {formData.is_active ? 'Visible on Landing Page' : 'Hidden'}
                                            </span>
                                        </label>
                                    </div>

                                    <div className="flex gap-4 pt-4 border-t border-white/10">
                                        <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">
                                            Cancel
                                        </Button>
                                        <Button type="submit" className="flex-1 bg-gradient-to-r from-gold-600 to-gold-500 text-black font-bold hover:shadow-lg hover:shadow-gold-500/20">
                                            {editingEvent ? 'Save Changes' : 'Create Event'}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminEvents;
